import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function HydrogenIonCalculator() {
  const [ph, setPh] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Hydrogen Ion Calculator", url: "https://calclab.app/calculator/hydrogen-ion" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Hydrogen Ion Calculator",
      "Calculate H+ concentration from pH. Free online chemistry calculator with instant results and detailed explanations.",
      "https://calclab.app/calculator/hydrogen-ion",
      lang
    )
  ]);

  const calculate = () => {
    const phValue = parseFloat(ph);

    if (isNaN(phValue) || phValue < 0 || phValue > 14) {
      toast({
        title: "Invalid Input",
        description: "Please enter a pH value between 0 and 14.",
        variant: "destructive",
      });
      return;
    }

    const hConcentration = Math.pow(10, -phValue);
    setResult(hConcentration);

    toast({
      title: "Calculation Complete",
      description: `[H⁺] = ${hConcentration.toExponential(2)} M`,
    });
  };

  const reset = () => {
    setPh("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Hydrogen Ion Concentration Calculator" 
      calculatorPath="/calculator/hydrogen-ion"
      category="chemistry"
      subcategory="Acids & Bases"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Hydrogen Ion Concentration Calculator</CardTitle>
            <CardDescription>
              Calculate [H⁺] from pH
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ph">pH</Label>
                <Input
                  id="ph"
                  type="number"
                  step="any"
                  value={ph}
                  onChange={(e) => setPh(e.target.value)}
                  placeholder="Enter pH (0-14)"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">[H⁺] Concentration</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toExponential(2)} M</p>
                    <CopyResultButton value={`${result.toExponential(2)} M`} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">[H⁺] = 10⁻ᵖᴴ</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
